import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { agentAPI, appointmentAPI, bookingAPI, claimAPI, walletAPI } from '../../lib/api';
import { formatCurrency, getRankLabel, generateReferralLink } from '../../lib/utils';
import { GlassCard, StatCard, StatusBadge, RankBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2, Calendar, FileText, Wallet, Users, Copy,
  ArrowRight, TrendingUp, DollarSign, Award, Target,
} from 'lucide-react';

const AgentDashboard = () => {
  const { user } = useAuth();
  const [performance, setPerformance] = useState(null);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const [perfRes, apptsRes, bookingsRes, walletRes] = await Promise.all([
        agentAPI.getPerformance(user.id).catch(() => ({ data: {} })),
        appointmentAPI.getAppointments({ status: 'pending' }).catch(() => ({ data: [] })),
        bookingAPI.getBookings().catch(() => ({ data: [] })),
        walletAPI.getWallet().catch(() => ({ data: { balance: 0 } })),
      ]);
      setPerformance(perfRes.data);
      setPendingAppointments(Array.isArray(apptsRes.data) ? apptsRes.data.slice(0, 5) : []);
      setRecentBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data.slice(0, 5) : []);
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = generateReferralLink(user?.referral_code);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const rankProgress = () => {
    const rankOrder = ['neg', 'rm', 'avp', 'svp', 'evp'];
    const currentIdx = rankOrder.indexOf(user?.agent_rank || 'neg');
    const criteria = { neg: 0, rm: 5, avp: 15, svp: 30, evp: 50 };
    const sales = performance?.completed_sales || 0;
    const nextRank = rankOrder[currentIdx + 1];
    if (!nextRank) return { progress: 100, nextRank: null, required: 0, current: sales };
    const currentRequired = criteria[user?.agent_rank || 'neg'];
    const nextRequired = criteria[nextRank];
    const progress = Math.min(100, ((sales - currentRequired) / (nextRequired - currentRequired)) * 100);
    return { progress, nextRank, required: nextRequired, current: sales };
  };

  const rankInfo = rankProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="agent-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Dashboard</h1>
          <div className="flex items-center gap-3 mt-2">
            <RankBadge rank={user?.agent_rank} />
            <span className="font-mono text-[#4E50FF] font-medium">{user?.agent_code}</span>
          </div>
        </div>
        <Button onClick={copyReferralLink} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="copy-referral-btn">
          <Copy className="w-4 h-4 mr-2" />
          Copy Referral Link
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Total Sales" value={performance?.completed_sales || 0} icon={TrendingUp} />
        <StatCard title="Pending Appointments" value={performance?.pending_appointments || 0} icon={Calendar} />
        <StatCard title="Total Commission" value={formatCurrency(performance?.total_commission_earned || 0)} icon={DollarSign} />
        <StatCard title="Wallet Balance" value={formatCurrency(walletBalance)} subtitle="Points" icon={Wallet} />
      </div>

      {/* Rank Progress */}
      {rankInfo.nextRank && (
        <GlassCard className="border-l-4 border-l-amber-400">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-lg bg-amber-50">
              <Award className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Rank Progress</h3>
              <p className="text-sm text-gray-500">
                {rankInfo.current} / {rankInfo.required} sales to {getRankLabel(rankInfo.nextRank)}
              </p>
            </div>
          </div>
          <Progress value={rankInfo.progress} className="h-2" />
        </GlassCard>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pending Appointments */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pending Appointments</h2>
            <Link to="/appointments">
              <Button variant="ghost" className="text-[#4E50FF] hover:text-[#3436CC]">
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <GlassCard className="space-y-3">
            {pendingAppointments.length > 0 ? (
              pendingAppointments.map((appt) => (
                <div key={appt.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{appt.customer?.full_name || 'Customer'}</p>
                    <p className="text-sm text-gray-500">{appt.property?.name || 'Property'}</p>
                  </div>
                  <StatusBadge status={appt.status} />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400">No pending appointments</p>
              </div>
            )}
          </GlassCard>
        </div>

        {/* Recent Bookings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
            <Link to="/bookings">
              <Button variant="ghost" className="text-[#4E50FF] hover:text-[#3436CC]">
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <GlassCard className="space-y-3">
            {recentBookings.length > 0 ? (
              recentBookings.map((booking) => (
                <div key={booking.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{booking.buyer_name || 'Buyer'}</p>
                    <p className="text-sm text-gray-500">{booking.property?.name || booking.manual_unit_number || 'Property'}</p>
                  </div>
                  <StatusBadge status={booking.status} />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400">No bookings yet</p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Quick Actions */}
      <GlassCard>
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Link to="/agent-properties">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-properties">
              <Building2 className="w-4 h-4 mr-2" />
              Properties
            </Button>
          </Link>
          <Link to="/appointments">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Appointments
            </Button>
          </Link>
          <Link to="/bookings">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-bookings">
              <FileText className="w-4 h-4 mr-2" />
              Bookings
            </Button>
          </Link>
          <Link to="/commission-claims">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-claims">
              <DollarSign className="w-4 h-4 mr-2" />
              Claims
            </Button>
          </Link>
          <Link to="/wallet">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-wallet">
              <Wallet className="w-4 h-4 mr-2" />
              Wallet
            </Button>
          </Link>
        </div>
      </GlassCard>
    </div>
  );
};

export default AgentDashboard;
